import { Box, SxProps, Theme, Typography } from '@mui/material'
import { useMinimalsTheme } from '@tom-ui/ui'
import { BayGap } from '../../helpers/yard-view.utils'
import { ReeferRailCell } from './ReeferRailCell'

type BayGapVariant = 'plain' | 'filled' | 'outlined' | 'ghost'

interface Props {
  gap: BayGap
  variant: BayGapVariant
  /** Pass-through highlighted state for the reefer-kind 'outlined' surface. */
  highlighted?: boolean
  sx?: SxProps<Theme>
}

/** Formats a meter value with one decimal place, dropping a trailing `.0`. */
export const formatMeters = (value: number): string => {
  const rounded = Math.round(value * 10) / 10
  return Number.isInteger(rounded) ? `${rounded}m` : `${rounded.toFixed(1)}m`
}

export const BayGapCell = ({ gap, variant, highlighted, sx }: Props) => {
  const theme = useMinimalsTheme()

  if (gap.kind === 'reefer') {
    if (variant === 'plain') {
      return (
        <Box
          sx={{
            width: gap.widthPx,
            flexShrink: 0,
            backgroundColor: theme.palette.common.white,
            ...sx,
          }}
        />
      )
    }
    const renderIcons = variant === 'outlined' || variant === 'ghost'
    return (
      <ReeferRailCell
        showLeftIcon={renderIcons && gap.hasAft}
        showRightIcon={renderIcons && gap.hasFore}
        variant={variant}
        highlighted={highlighted}
        sx={{ width: gap.widthPx, flexShrink: 0, ...sx }}
      />
    )
  }

  const showLabel = variant === 'outlined' || variant === 'ghost'

  return (
    <Box
      sx={{
        width: gap.widthPx,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderLeft: `1px dashed ${theme.palette.secondary.main}`,
        borderRight: `1px dashed ${theme.palette.secondary.main}`,
        ...sx,
      }}
    >
      {showLabel && (
        <Typography
          variant='mini'
          sx={{
            color: variant === 'ghost' ? theme.palette.grey[500] : theme.palette.secondary.main,
            whiteSpace: 'nowrap',
          }}
        >
          {formatMeters(gap.widthInMeters)}
        </Typography>
      )}
    </Box>
  )
}
